import { useState } from 'react'

import { useAction } from '../components/toast'
import { Card, Kpi, Loaded, Modal, Notice, Page } from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, dateTime } from '../lib/format'
import { useResource } from '../lib/useResource'

type Status = {
  live_samples: number
  archived_days: number
  oldest_live: string | null
  oldest_archived: string | null
  keep_days: number
}

type ArchiveResult = {
  cutoff: string
  days_archived: number
  samples_removed: number
}

/** The API refuses anything shorter, so say so before the round trip. */
const MIN_REASON = 10

export function Retention() {
  const { can } = useAuth()
  const canRun = can(P.configWrite)

  const status = useResource((signal) =>
    get<Status>('/v1/admin/retention/status', undefined, signal),
  )
  const [running, setRunning] = useState(false)

  return (
    <Page
      title="Retention & archive"
      subtitle="How much full-resolution health data is held, and how far back it goes."
      actions={
        canRun && (
          <button className="btn btn--sm btn--primary" onClick={() => setRunning(true)}>
            Run archive
          </button>
        )
      }
    >
      <div className="stack">
        <Loaded resource={status}>
          {(data) => (
            <>
              <div className="grid grid--kpi">
                <Kpi label="Live samples" value={count(data.live_samples)} />
                <Kpi label="Archived days" value={count(data.archived_days)} />
                <Kpi label="Kept at full resolution" value={`${data.keep_days} days`} />
                <Kpi
                  label="Oldest live reading"
                  value={data.oldest_live ? dateTime(data.oldest_live) : '—'}
                  foot={
                    data.oldest_archived
                      ? `Archived back to ${data.oldest_archived}`
                      : 'Nothing archived yet'
                  }
                />
              </div>

              <Card title="What archiving does">
                <div className="stack" style={{ padding: 16 }}>
                  <p className="muted" style={{ margin: 0 }}>
                    Readings older than <strong>{data.keep_days} days</strong> are summarised
                    into one row per person per day — averages, minimums and maximums — and the
                    individual samples are removed. The day itself survives; the resolution
                    does not.
                  </p>
                  <Notice>
                    This is deliberately manual. It rewrites the largest table in the database,
                    so an operator should choose when it happens rather than discover it during
                    an incident. Running it twice is safe — a day already archived is skipped.
                  </Notice>
                  {!canRun && (
                    <Notice tone="warn">
                      Running it needs <span className="mono">config:write</span>. You can see
                      the numbers but not start a run.
                    </Notice>
                  )}
                </div>
              </Card>
            </>
          )}
        </Loaded>
      </div>

      {running && (
        <RunArchive
          onClose={() => setRunning(false)}
          onDone={() => {
            setRunning(false)
            status.reload()
          }}
        />
      )}
    </Page>
  )
}

function RunArchive({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const run = useAction()
  const [reason, setReason] = useState('')
  const [keepDays, setKeepDays] = useState(90)
  const [busy, setBusy] = useState(false)

  // The server refuses under 30; saying so here avoids a round trip that comes
  // back as a bare 400.
  const tooShort = keepDays < 30
  const canRun = reason.trim().length >= MIN_REASON && !tooShort && !busy

  const go = async () => {
    setBusy(true)
    await run(
      async () => {
        const result = await post<ArchiveResult>('/v1/admin/retention/archive', {
          reason: reason.trim(),
          keep_days: keepDays,
        })
        return result
      },
      'Archive finished.',
      onDone,
    )
    setBusy(false)
  }

  return (
    <Modal title="Run the archive" onClose={onClose}>
      <div className="stack">
        <Notice tone="warn">
          Individual readings older than the window below are replaced by a daily summary and
          cannot be recovered. The summary keeps the day, the averages and the extremes — not
          the individual samples.
        </Notice>

        <div className="field">
          <label className="field__label" htmlFor="keep-days">
            Keep full resolution for (days)
          </label>
          <input
            id="keep-days"
            className="input"
            type="number"
            min={30}
            max={3650}
            value={keepDays}
            onChange={(event) => setKeepDays(Number(event.target.value))}
          />
          {tooShort && (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              At least 30 days — the server refuses anything shorter.
            </p>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="archive-reason">
            Why are you running it?
          </label>
          <input
            id="archive-reason"
            className="input"
            value={reason}
            autoFocus
            placeholder="Quarterly archive — vital_samples is at 40M rows"
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--danger" disabled={!canRun} onClick={() => void go()}>
            {busy ? 'Archiving…' : 'Run archive'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
